// src/components/SuperAdmin/ProjectDashboard.js
import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Backsheet, Button, FormInput } from '../ui/UIComponents';
import SwitchToggle from '../ui/SwitchToggle';
import { toast } from 'react-hot-toast';
import { Edit2, FileText } from 'lucide-react';
import ProjectDetails from './ProjectDetails';

export default function BlogDashboard() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isOpen, setIsOpen] = useState(false);
    const [formData, setFormData] = useState({
        slug: '',
        sequence: '',
        project_tag: '',
        project_type: '',
        name: '',
        developer: '',
        city: '',
        zone: '',
        location: '',
        configuration: '',
        property_type: '',
        price: '',
        area_min: '',
        area_max: '',
        area_unit: '',
        status: '',
        image: ''
    });
    const [originalSlug, setOriginalSlug] = useState(null);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [detailsSlug, setDetailsSlug] = useState('');

    const toggleCompletion = async (slug, newValue) => {
        try {

            // If they’re trying to turn it ON, verify RERA exists
            if (newValue) {
                const projSnap = await getDoc(doc(db, 'projects', slug));
                const projData = projSnap.exists() ? projSnap.data() : {};
                if (!projData.project_rera || !projData.project_rera.toString().trim()) {
                    toast.error('Cannot mark complete until RERA Number is set.');
                    return;
                }
            }
            // 1) Persist to the individual project doc
            await setDoc(
                doc(db, 'projects', slug),
                { project_details_complete: newValue },
                { merge: true }
            );

            // 2) Also update the aggregate highlights array
            const aggSnap = await getDoc(aggRef);
            if (aggSnap.exists()) {
                const arr = aggSnap.data().projectHighlights || [];
                const updated = arr.map(item =>
                    item.slug === slug
                        ? { ...item, project_details_complete: newValue }
                        : item
                );
                await setDoc(aggRef, { projectHighlights: updated });
            }

            // 3) Optimistically update UI
            setProjects(ps =>
                ps.map(p =>
                    p.slug === slug ? { ...p, project_details_complete: newValue } : p
                )
            );

            toast.success(`Marked details ${newValue ? 'complete' : 'incomplete'}`);
        } catch (e) {
            console.error('Toggle failed', e);
            toast.error('Could not update flag');
        }
    };


    const aggRef = doc(db, 'propxplorer', 'project-highlights');

    const fetchProjects = async () => {
        setLoading(true);
        try {
            const snap = await getDoc(aggRef);
            let list = [];
            if (snap.exists()) {
                const data = snap.data();
                if (Array.isArray(data.projectHighlights)) {
                    list = data.projectHighlights;
                } else {
                    list = Object.entries(data).map(([slug, rec]) => ({ slug, ...rec }));
                }
                list.sort((a, b) => Number(a.sequence) - Number(b.sequence));
            }
            setProjects(list);
        } catch (e) {
            console.error('Fetch failed', e);
            toast.error('Failed to load projects');
            setProjects([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProjects();
    }, []);

    const openForEdit = (proj) => {
        setOriginalSlug(proj.slug);
        setFormData({
            ...proj,
            project_tag: Array.isArray(proj.project_tag)
                ? proj.project_tag.join(', ')
                : proj.project_tag
        });
        setIsOpen(true);
    };

    const openForAdd = () => {
        setOriginalSlug(null);
        setFormData({
            slug: '',
            sequence: '',
            project_tag: '',
            project_type: '',
            name: '',
            developer: '',
            city: '',
            zone: '',
            location: '',
            configuration: '',
            property_type: '',
            price: '',
            area_min: '',
            area_max: '',
            area_unit: '',
            status: '',
            image: ''
        });
        setIsOpen(true);
    };

    const handleSave = async () => {
        const {
            slug,
            sequence,
            project_tag,
            project_type,
            name,
            developer,
            city,
            zone,
            location,
            configuration,
            property_type,
            price,
            area_min,
            area_max,
            area_unit,
            status,
            image
        } = formData;
        const required = [
            'slug',
            'project_type',
            'name',
            'developer',
            'city',
            'zone',
            'location',
            'configuration',
            'property_type',
            'price',
            'area_min',
            'area_max',
            'area_unit',
            'status',
            'image'
        ];
        const missing = required.filter(
            (field) => !formData[field] || !formData[field].toString().trim()
        );
        if (missing.length) {
            toast.error(`Please fill: ${missing.join(', ')}`);
            return;
        }
        const slugExists = projects.some((p) => p.slug === slug);
        if (
            (!originalSlug && slugExists) ||
            (originalSlug && slug !== originalSlug && slugExists)
        ) {
            toast.error('Slug must be unique');
            return;
        }
        const payload = {
            slug,
            sequence,
            project_tag: project_tag
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            project_type,
            name,
            developer,
            city,
            zone,
            location,
            configuration,
            property_type,
            price,
            area_min,
            area_max,
            area_unit,
            status,
            image,
            project_details_complete: formData.project_details_complete || false,
        };
        try {
            // update per-project details doc
            await setDoc(doc(db, 'projects', slug), payload, { merge: true });
            // update aggregate highlights doc
            const existing = projects.filter(
                (p) => p.slug !== (originalSlug || slug)
            );
            existing.push(payload);
            existing.sort((a, b) => Number(a.sequence) - Number(b.sequence));
            await setDoc(aggRef, { projectHighlights: existing });

            toast.success('Project saved');
            setIsOpen(false);
            fetchProjects();
        } catch (e) {
            console.error('Save failed', e);
            toast.error('Save failed');
        }
    };

    const handleDelete = async (slugToDelete) => {
        if (!window.confirm(`Delete project '${slugToDelete}'?`)) return;
        try {
            // delete per-project details doc
            await deleteDoc(doc(db, 'projects', slugToDelete));
            // update aggregate highlights
            const filtered = projects.filter((p) => p.slug !== slugToDelete);
            await setDoc(aggRef, { projectHighlights: filtered });

            toast.success('Project deleted');
            setIsOpen(false);
            fetchProjects();
        } catch (e) {
            console.error('Delete failed', e);
            toast.error('Delete failed');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((fd) => ({ ...fd, [name]: value }));
    };

    const openDetails = (slug) => {
        setDetailsSlug(slug);
        setIsDetailsOpen(true);
    };

    return (
        <div className="min-h-screen">
            <header className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold">Project Dashboard</h2>
                <Button onClick={openForAdd}>Add Project</Button>
            </header>

            {loading ? (
                <p className="text-center">Loading projects…</p>
            ) : projects.length === 0 ? (
                <p className="text-center">
                    No projects found. Click “Add Project” to get started.
                </p>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {projects.map((proj) => (
                        <div key={proj.slug} className="bg-white p-4 rounded shadow">
                            {proj.image && (
                                <img
                                    src={proj.image}
                                    alt={proj.name}
                                    className="h-24 w-full object-cover mb-2 rounded"
                                />
                            )}
                            <h3 className="text-xl font-medium">{proj.name || proj.slug}</h3>
                            <p className="text-sm text-gray-500">
                                Sequence: {proj.sequence}
                            </p>
                            <p className="mt-2 text-gray-700 text-sm">
                                {proj.project_type}, {proj.configuration}
                            </p>
                            <div className="mt-4 flex space-x-2">
                                <Button
                                    onClick={() => openForEdit(proj)}
                                    variant="outline"
                                    className="p-2 rounded bg-indigo-700 hover:bg-indigo-900"
                                >
                                    <Edit2 size={10} />
                                </Button>
                                <Button
                                    onClick={() => openDetails(proj.slug)}
                                    variant="outline"
                                    className="p-2 rounded bg-indigo-700 hover:bg-indigo-900"
                                >
                                    <FileText size={10} />
                                </Button>
                                {/* line ~84: “Details Complete” toggle */}
                                <SwitchToggle
                                    checked={proj.project_details_complete || false}
                                    onChange={() =>
                                        toggleCompletion(proj.slug, !proj.project_details_complete)
                                    }
                                />
                            </div>

                        </div>
                    ))}
                </div>
            )}

            {/* Highlights Backsheet */}
            <Backsheet
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                title={formData.slug ? 'Edit Project' : 'Add Project'}
            >
                <div className="space-y-4">
                    <FormInput
                        label="Slug"
                        name="slug"
                        value={formData.slug}
                        onChange={handleChange}
                        required
                    />
                    <FormInput
                        label="Sequence"
                        name="sequence"
                        type="number"
                        value={formData.sequence}
                        onChange={handleChange}
                        required
                    />
                    <FormInput
                        label="Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                    />
                    <FormInput
                        label="Project Tags (comma-sep)"
                        name="project_tag"
                        value={formData.project_tag}
                        onChange={handleChange}
                    />
                    <FormInput
                        label="Type"
                        name="project_type"
                        value={formData.project_type}
                        onChange={handleChange}
                        required
                    />
                    <FormInput
                        label="Developer"
                        name="developer"
                        value={formData.developer}
                        onChange={handleChange}
                        required
                    />
                    <FormInput
                        label="City"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                    />
                    <FormInput
                        label="Zone"
                        name="zone"
                        value={formData.zone}
                        onChange={handleChange}
                        required
                    />
                    <FormInput
                        label="Location"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        required
                    />
                    <FormInput
                        label="Configuration"
                        name="configuration"
                        value={formData.configuration}
                        onChange={handleChange}
                        required
                    />
                    <FormInput
                        label="Property Type"
                        name="property_type"
                        value={formData.property_type}
                        onChange={handleChange}
                        required
                    />
                    <FormInput
                        label="Price"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        required
                    />
                    <FormInput
                        label="Area Min"
                        name="area_min"
                        value={formData.area_min}
                        onChange={handleChange}
                        required
                    />
                    <FormInput
                        label="Area Max"
                        name="area_max"
                        value={formData.area_max}
                        onChange={handleChange}
                        required
                    />
                    <FormInput
                        label="Area Unit"
                        name="area_unit"
                        value={formData.area_unit}
                        onChange={handleChange}
                        required
                    />
                    <FormInput
                        label="Status"
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                        required
                    />
                    <FormInput
                        label="Image URL"
                        name="image"
                        value={formData.image}
                        onChange={handleChange}
                        required
                    />
                    <div className="flex justify-between">
                        {formData.slug && (
                            <Button
                                variant="danger"
                                onClick={() => handleDelete(formData.slug)}
                            >
                                Delete
                            </Button>
                        )}
                        <div className="ml-auto flex space-x-2">
                            <Button variant="secondary" onClick={() => setIsOpen(false)}>
                                Cancel
                            </Button>
                            <Button onClick={handleSave}>Save</Button>
                        </div>
                    </div>
                </div>
            </Backsheet>

            {/* Details Backsheet in separate file */}
            <ProjectDetails
                slug={detailsSlug}
                isOpen={isDetailsOpen}
                onClose={() => setIsDetailsOpen(false)}
                refreshHighlights={fetchProjects}
            />
        </div>
    );
}
